import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { FaEye, FaPen, FaUserCog } from "react-icons/fa";
import styles from "./examResult.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton, CustomDropdown } from "../../../../components";
import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
import {
  clearSectionOptions,
  fetchClassOptions,
  fetchSectionOptions,
  fetchStaffClassAndSubjects,
} from "../../../../redux/Masterdata";
import dayjs from "dayjs";

const ExamResult = () => {
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const roleId = Number(user?.role?.id);
  const user_id = user?.id || "";
  // const master_id = clientData?.master_id || "";
  const staff_id = clientData?.master_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";

  /* ── Student (4) / Parent (6): class & section come from the cookie ──
        Their class is already in client_data, so the class/section lists are
        built from it instead of the master-data APIs. Sessions whose cookie
        lacks these fields fall back to the API path below. */
  const isStudentOrParent = roleId === 4 || roleId === 6;
  const cookieClassId =
    clientData?.class_id != null ? String(clientData.class_id) : "";
  const cookieClassName = clientData?.class_name || clientData?.class || "";
  const cookieSectionId =
    clientData?.student_section || clientData?.section || "";
  const useCookieScope =
    isStudentOrParent &&
    Boolean(cookieClassId && cookieClassName && cookieSectionId);

  // Staff (3): only the classes assigned to the logged-in staff member.
  const isStaff = roleId === 3;

  const { translations } = useSelector((state) => state.language);
  // const navigate = useNavigate();
  const dispatch = useDispatch();
  const [submitted, setSubmitted] = useState(false);
  const [selectedExamName, setSelectedExamName] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [examData, setExamData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ NEW: tracks whether search has been submitted with no results
  const [noResultsFound, setNoResultsFound] = useState(false);

  const [errors, setErrors] = useState({});
  const {
    sectionOptions: reduxSectionOptions,
    classOptions,
    staffClassAndSubjectOptions,
  } = useSelector((state) => state.masterData);

  // Staff data has one row per section/subject – keep a single entry per class
  const staffClassOptions = useMemo(
    () =>
      Array.from(
        new Map(
          (staffClassAndSubjectOptions || [])
            .filter((item) => item.class_id != null)
            .map((item) => [
              String(item.class_id),
              { value: String(item.class_id), label: item.class_name },
            ]),
        ).values(),
      ),
    [staffClassAndSubjectOptions],
  );

  const examOptions = examData.map((item) => ({
    label: item.exam_type,
    value: item.id,
  }));

  // Only the logged-in child's class for Student/Parent, the staff member's
  // assigned classes for Staff, the full list otherwise.
  const classOptionsToShow = useMemo(() => {
    if (useCookieScope) {
      return [{ value: cookieClassId, label: cookieClassName }];
    }
    if (isStaff) return staffClassOptions;
    return classOptions;
  }, [
    useCookieScope,
    cookieClassId,
    cookieClassName,
    isStaff,
    staffClassOptions,
    classOptions,
  ]);

  const sectionOptions = useMemo(() => {
    if (useCookieScope) {
      return [{ value: cookieSectionId, label: cookieSectionId }];
    }
    return [
      {
        value: "",
        label: translations.labels?.selectSection || "Select Section",
      },
      ...reduxSectionOptions,
    ];
  }, [useCookieScope, cookieSectionId, reduxSectionOptions, translations]);

  const validateForm = () => {
    const newErrors = {};
    if (!selectedClass) newErrors.class = translations.errors.classRequired;
    if (!selectedExamName)
      newErrors.examName = translations.errors.sectionRequired;
    if (!selectedSection)
      newErrors.section = translations.errors.sectionRequired;
    return newErrors;
  };

  const handleExamNameChange = (value) => {
    setSelectedExamName(value);
    setErrors((prev) => ({ ...prev, examName: "" }));
  };

  const handleClassChange = useCallback(
    (value) => {
      const classId = typeof value === "object" ? value?.value : value;
      setSelectedClass(classId);
      setErrors((prev) => ({ ...prev, class: "" }));

      // Cookie-scoped roles have a single class; keep their section as-is and
      // never hit the section API.
      if (useCookieScope) return;

      setSelectedSection("");
      dispatch(clearSectionOptions());
      if (classId) {
        dispatch(
          fetchSectionOptions({
            id: parseInt(classId),
            school_id: parseInt(school_id),
            staff_id: parseInt(staff_id),
            subject_staff: 1,
          }),
        );
      }
    },
    [dispatch, school_id, staff_id, useCookieScope],
  );

  const handleSectionChange = (value) => {
    setSelectedSection(value);
    setErrors((prev) => ({ ...prev, section: "" }));
  };

  const fetchExamConfigList = async () => {
    setLoading(true);
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
      };
      const response = await AxiosInstance.post(
        `/exam/examConfig/list`,
        payload,
      );
      if (response?.data?.status) {
        const examData = response.data.data || [];
        const transformedData = examData.map((item, index) => ({
          id: item.id,
          sno: index + 1,
          academic_id: item.academic_id,
          academic_year: item.academic_year,
          exam_type: item.exam_type,
          start_date: item.start_date
            ? dayjs(item.start_date).format("DD-MM-YYYY")
            : "",
          end_date: item.end_date
            ? dayjs(item.end_date).format("DD-MM-YYYY")
            : "",
          isActive: item.isActive,
        }));
        setExamData(transformedData);
      } else {
        setExamData([]);
      }
    } catch (error) {
      console.error("Error fetching exam config list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExamConfigList();
    // Student/Parent read their class from the cookie — no class list call.
    if (useCookieScope) return;
    if (isStaff) {
      dispatch(
        fetchStaffClassAndSubjects({
          staff_id: parseInt(staff_id),
          class_id: "",
          section_id: "",
          subject_id: "",
        }),
      );
      return;
    }
    dispatch(
      fetchClassOptions({
        id: parseInt(school_id),
        academic_year: academic_year,
      }),
    );
  }, [dispatch, school_id, academic_year, useCookieScope, isStaff, staff_id]);

  // Pre-select the cookie class/section for Student & Parent.
  useEffect(() => {
    if (!useCookieScope) return;
    setSelectedClass(cookieClassId);
    setSelectedSection({ value: cookieSectionId, label: cookieSectionId });
  }, [useCookieScope, cookieClassId, cookieSectionId]);

  const handleCancel = () => {
    setSelectedExamName("");
    // Reset to the cookie values instead of blanking the only option available.
    setSelectedClass(useCookieScope ? cookieClassId : "");
    setSelectedSection(
      useCookieScope
        ? { value: cookieSectionId, label: cookieSectionId }
        : "",
    );
    // ✅ Also reset no-results state on cancel
    setNoResultsFound(false);
    setTableData([]);
  };

  const handleSubmit = async (e) => {
    setSubmitted(true);
    e.preventDefault();
    const validationErrors = validateForm();
    let finalStaffId = "";
    let finalUserId = "";
    let finalParentId = "";

    if (roleId === 3) {
      finalStaffId = staff_id;
    } else if (roleId === 4) {
      finalUserId = user_id;
    } else if (roleId === 6) {
      finalParentId = user_id;
    }

    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      const payload = {
        school_id: school_id,
        class_id: selectedClass || "",
        section_id: selectedSection.value || "",
        config_id: selectedExamName.value || "",
        user_id: finalUserId,
        staff_id: finalStaffId,
        parent_id: finalParentId,
      };
      const response = await AxiosInstance.post(
        `/exam/examPublish/RankCard`,
        payload,
      );
      if (response.data?.status) {
        setTableData(response.data.data);
        setNoResultsFound(false); // ✅ clear no-results on success
      } else {
        setTableData([]);
        setNoResultsFound(true); // ✅ flag: API returned status false or empty data
      }
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>
          {translations?.labels?.examResult || "Exam Result"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="row g-3 align-items-center">
        {/* Exam Name */}
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={translations.labels.examName}
            name="examName"
            value={selectedExamName}
            onChange={handleExamNameChange}
            options={examOptions}
            placeholder={translations.labels.selectExamName}
            required
            error={submitted && errors.examName}
            isSearchable={false}
          />
        </div>
        {/* Class */}
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={translations.labels.class}
            name="classValue"
            value={selectedClass}
            onChange={handleClassChange}
            options={classOptionsToShow}
            placeholder={translations.labels.selectClass}
            required
            error={submitted && errors.class}
            isSearchable={false}
          />
        </div>
        {/* Section */}
        <div className="col-lg-2 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={translations.labels.section}
            name="section"
            value={selectedSection}
            options={sectionOptions}
            onChange={handleSectionChange}
            placeholder={translations.labels.selectSection}
            required
            error={submitted && errors.section}
            isSearchable={false}
          />
        </div>

        <div className="col-md-3 px-2 d-flex align-items-end">
          <div className="d-flex gap-2 w-100">
            <CustomButton
              variant="contained"
              onClick={handleSubmit}
              className={styles.saveButton}
              loading={loading}
            >
              {translations.labels.submit || "Submit"}
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={handleCancel}
              className={styles.cancelButton}
              disabled={loading}
            >
              {translations.labels.back || "Back"}
            </CustomButton>
          </div>
        </div>
      </form>

      {/* ===========================
          NO RESULTS MESSAGE
      =========================== */}
      {noResultsFound && (
        <div className={styles.noResultsMessage}>
          {translations?.labels?.noExamResultFound ||
            "There is no Exam Result for this class."}
        </div>
      )}

      {/* ===========================
          TABLE (LOAD AFTER SELECT)
      =========================== */}
      {tableData && Object.keys(tableData).length > 0 && (
        <>
          <div className={styles.wrapper}>
            <div className={styles.card}>
              {tableData?.students?.map((item) => (
                <>
                  {/* Header */}
                  <div className={`${styles.headerTitleRank} pt-5`}>
                    <div className={styles.headerLeft}>
                      <h2>{tableData.school_name || "-"}</h2>
                    </div>
                    <div className={styles.headerCenter}>
                      <p>{tableData.address || "-"}</p>
                      <p>
                        Phone: {tableData.phone_number || "-"} | Email:{" "}
                        {tableData.email || "-"}
                      </p>
                    </div>
                    <div className={styles.headerRight}>
                      <h3 className={styles.rankTitle}>
                        Rank Card : {tableData.academic_year || "-"}
                      </h3>
                    </div>
                  </div>

                  <div className={styles.divider}></div>

                  <div key={item.student_id}>
                    {/* Student Info Row */}
                    <div className={styles.infoRow}>
                      <div className={styles.label}>Student Name</div>
                      <div className={styles.value}>
                        {item.student_name || "-"}
                      </div>
                      <div className={styles.label}>Class</div>
                      <div className={styles.value}>
                        {item.class_name || "-"} - {item.section_id || "-"}
                      </div>
                      <div className={styles.label}>Exam</div>
                      <div className={styles.value}>
                        {item.exam_type || "-"}
                      </div>
                    </div>

                    {/* Marks Table */}
                    <div className={styles.table}>
                      <div className={`${styles.row} ${styles.tableHeader}`}>
                        <div>Subjects</div>
                        <div>Theory</div>
                        <div>Practical</div>
                        <div>Total</div>
                      </div>
                      {item.subject_details?.map((subject) => (
                        <div className={styles.row} key={subject.subject_id}>
                          <div>{subject.subject_name}</div>
                          <div>{subject.theory ?? "-"}</div>
                          <div>{subject.practical ?? "-"}</div>
                          <div>{subject.total ?? 0}</div>
                        </div>
                      ))}
                      {/* Total Row */}
                      <div className={`${styles.row} ${styles.totalRow}`}>
                        <div className={styles.totalLabel}>
                          Total Maximum Marks
                        </div>
                        <div>{item.total_max_marks || "-"}</div>
                        <div>{item.overall_marks || "-"}</div>
                      </div>
                    </div>

                    {/* Result Row */}
                    <div className={styles.resultRow}>
                      <div className={styles.label}>Result</div>
                      <div
                        className={`${styles.value} ${
                          item.result === "PASS" ? styles.pass : styles.fail
                        }`}
                      >
                        {item.result}
                      </div>
                      <div className={styles.label}>Rank</div>
                      <div className={styles.value}>{item.rank || "-"}</div>
                    </div>
                  </div>
                </>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExamResult;
